import { AutoComplete, Box, FormField, listItemSelectBuilder, Text } from "@wix/design-system";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useBiNext } from "./../../../bi-context-provider/bi-context-provider.js";
import { EditorBaseUrlContext } from "./../../../editor-base-url/editor-base-url-context.js";
import { LINK_MODAL_ID } from "./../../../link-modal/constants.js";
import { removeBaseUrl } from "./../../../url/remove-base-url.js";
import { SITE_PAGES_EMPTY_STATE, SITE_PAGES_PAGE_SELECT } from "../../data-hooks/data-hooks.js";
import { useSitePages } from "./use-site-pages.js";
const typeTranslations = {
  AutoComplete_Label: {
    blogPost: "LinkSettings_SitePages_BlogPost_AutoComplete_Label",
    staticPage: "LinkSettings_SitePages_StaticPage_AutoComplete_Label"
  },
  AutoComplete_Placeholder: {
    blogPost: "LinkSettings_SitePages_BlogPost_AutoComplete_Placeholder",
    staticPage: "LinkSettings_SitePages_StaticPage_AutoComplete_Placeholder"
  },
  EmptyState_Text1: {
    blogPost: "LinkSettings_SitePages_BlogPost_EmptyState_Text1",
    staticPage: "LinkSettings_SitePages_StaticPage_EmptyState_Text1"
  },
  EmptyState_Text2: {
    blogPost: "LinkSettings_SitePages_BlogPost_EmptyState_Text2",
    staticPage: "LinkSettings_SitePages_StaticPage_EmptyState_Text2"
  },
  NoResults_Any: {
    blogPost: "LinkSettings_SitePages_BlogPost_NoResults_Any",
    staticPage: "LinkSettings_SitePages_StaticPage_NoResults_Any"
  }
};
const pageToOption = (tooltipProps) => (page) => Object.assign(
  listItemSelectBuilder({
    ...page,
    ellipsis: true,
    titleMaxLines: 2,
    id: page.id,
    label: page.title,
    size: "small",
    dataHook: "option",
    subtitle: page.updatedDate?.toLocaleDateString() || page.description || void 0,
    tooltipProps: {
      ...tooltipProps,
      children: page.title,
      enterDelay: 1e3,
      placement: "top"
    }
  }),
  { page }
);
const SitePages = ({
  autoCompletePopoverProps,
  autoFocus,
  link,
  linkType,
  onSelect,
  onSubmit,
  t,
  tooltipProps
}) => {
  const { baseUrl } = useContext(EditorBaseUrlContext);
  const logBi = useBiNext({ pluginId: "link", modalId: LINK_MODAL_ID });
  const pagesState = useSitePages(linkType);
  const options = useMemo(
    () => pagesState.items.map(pageToOption(tooltipProps)),
    [pagesState.items, tooltipProps]
  );
  const [selectedOption, setSelectedOption] = useState(() => {
    const linkPath = removeBaseUrl(link.url || "", baseUrl);
    return options.find((option) => option.page.path === linkPath) || null;
  });
  const [value, setValue] = useState(selectedOption?.page.title || "");
  const ref = useRef(null);
  const onChange = (value2) => {
    pagesState.setQuery(value2);
    setSelectedOption(null);
    setValue(value2);
    onSelect("", "");
  };
  const onSelectOption = (option) => {
    pagesState.setQuery("");
    setSelectedOption(option);
    setValue(option.page.title);
    onSelect(option.page.path, option.page.title);
    logBi({ dataHook: SITE_PAGES_PAGE_SELECT, value: option.page.title });
  };
  const onEnterPressed = (evt) => {
    if (selectedOption) {
      onSubmit(evt);
    } else if (evt.target.getAttribute("data-hook")?.includes("option")) {
      setTimeout(() => onSubmit(evt));
    }
  };
  useEffect(() => {
    if (autoFocus) {
      ref.current?.showOptions();
    }
  }, []);
  if (pagesState.areAllFetched && !pagesState.query && !pagesState.items.length) {
    return /* @__PURE__ */ React.createElement(EmptyState, { linkType, t });
  }
  return /* @__PURE__ */ React.createElement(FormField, { label: t(typeTranslations.AutoComplete_Label[linkType]), labelPlacement: "top" }, /* @__PURE__ */ React.createElement(
    AutoComplete,
    {
      autoFocus,
      dataHook: SITE_PAGES_PAGE_SELECT,
      dropdownWidth: "auto",
      emptyStateMessage: pagesState.isLoading ? void 0 : /* @__PURE__ */ React.createElement(Text, { skin: "disabled", size: "small" }, pagesState.query ? t("LinkSettings_SitePages_All_NoResults_Query", { query: pagesState.query }) : t(typeTranslations.NoResults_Any[linkType])),
      focusOnSelectedOption: true,
      hasMore: pagesState.hasMore || pagesState.isLoading,
      infiniteScroll: true,
      loadMore: pagesState.loadMore,
      markedOption: selectedOption?.id,
      minWidthPixels: "252px",
      onChange: (evt) => onChange(evt.target.value),
      onEnterPressed,
      onSelect: onSelectOption,
      options,
      placeholder: t(typeTranslations.AutoComplete_Placeholder[linkType]),
      popoverProps: autoCompletePopoverProps,
      ref,
      selectedId: selectedOption?.id,
      size: "small",
      status: pagesState.error ? "error" : void 0,
      textOverflow: "ellipsis",
      value
    }
  ));
};
const EmptyState = ({ linkType, t }) => {
  return /* @__PURE__ */ React.createElement(
    Box,
    {
      dataHook: SITE_PAGES_EMPTY_STATE,
      direction: "vertical",
      margin: "SP4",
      gap: "SP2",
      verticalAlign: "middle",
      textAlign: "center"
    },
    /* @__PURE__ */ React.createElement(Text, { size: "small", weight: "bold" }, t(typeTranslations.EmptyState_Text1[linkType])),
    /* @__PURE__ */ React.createElement(Text, { size: "small", weight: "thin" }, t(typeTranslations.EmptyState_Text2[linkType]))
  );
};
export {
  SitePages
};
//# sourceMappingURL=site-pages.js.map
